<template>
  <div
    class="sf-full-width sf-below-2 sf-btn-bar"
    data-testid="sidebar-tabs-selector"
  >
    <CommunicationOverviewButton
      v-for="{
        active,
        disabled,
        type,
        icon,
        message,
        itemCount,
        // eslint-disable-next-line vue/no-unused-vars
        shown,
        ...rest
      } in items"
      :key="type"
      :active="active"
      :disabled="disabled"
      :item-count="itemCount"
      :icon="icon"
      :type="type"
      v-bind="rest"
      @item-clicked="handleItemClick"
    >
      {{ message }}
    </CommunicationOverviewButton>
  </div>
</template>

<script>
import { values } from 'lodash'
import { mapGetters, mapActions, mapState } from 'vuex'
import { mapState as mapPiniaState } from 'pinia'
import checkHelpers from './mixins/check-helpers'
import CommunicationOverviewButton from './communication-overview-button'
import {
  doneChecks,
  pendingChecks,
} from '@app/application/sf-assistant-checks/utils'

import { useChecksStore } from '@app/application/sf-assistant-checks/stores/checks'
import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'

export default {
  components: {
    CommunicationOverviewButton,
  },

  mixins: [checkHelpers],

  setup() {
    return {
      supportsCommunicationsRework:
        Silverfin.currentFirmFeatures.supports_communications_rework,
    }
  },

  computed: {
    ...mapGetters([
      'notes',
      'todos',
      'allChecks',
      'filteredSfAssistantRemarks',
      'notificationsByRemarkId',
    ]),

    ...mapState(['cardsFilter']),
    ...mapState({
      storeLedgerId: (state) => state.ledgerId,
    }),

    ...mapPiniaState(useChecksStore, ['checks']),
    ...mapPiniaState(useAuditsStore, ['changesSinceLastVisit']),

    allNotesCount() {
      return values(this.notes).length ? values(this.notes).length : 0
    },

    unreadNotesCount() {
      if (this.supportsCommunicationsRework) {
        let count = 0
        for (const note of values(this.notes)) {
          const notification = this.notificationsByRemarkId[note.id]
          if (notification && !notification.read) {
            count++
          }
        }
        return count
      } else {
        return 0
      }
    },

    allTodosCount() {
      return (
        (values(this.todos).length ? values(this.todos).length : 0) +
        this.doneSfAChecksCount +
        this.pendingSfAChecksCount
      )
    },

    pendingTodosCount() {
      return (
        values(this.todos).filter((todo) => !todo.done).length +
        this.pendingSfAChecksCount
      )
    },

    allChecksCount() {
      return values(this.allChecks).length ? values(this.allChecks).length : 0
    },

    pendingChecksCount() {
      return values(this.allChecks).filter(
        (check) => !this.checkCheckedByAllParticipants(check)
      ).length
    },

    allNotesMessage() {
      return this.I18n.t('communication.overview.notes_count', {
        count: this.allNotesCount,
      })
    },

    items() {
      return [
        {
          active: this.cardsFilter === 'note',
          disabled: this.allNotesCount === 0,
          icon: 'comments',
          itemCount: this.unreadNotesCount,
          message: this.allNotesMessage,
          type: 'note',
          shown: true,
        },
        {
          active: this.cardsFilter === 'todo',
          disabled: this.allTodosCount === 0,
          icon: 'bolt',
          itemCount: this.pendingTodosCount,
          message: this.I18n.t('communication.overview.todo_count', {
            count: this.allTodosCount,
          }),
          type: 'todo',
          shown: true,
        },
        {
          active: this.cardsFilter === 'checklist',
          disabled: this.allChecksCount === 0,
          icon: 'check-square',
          itemCount: this.pendingChecksCount,
          message: I18n.t('communication.overview.check_counts', {
            count: this.allChecksCount,
          }),
          type: 'checklist',
          shown: true,
        },
      ].filter((item) => item.shown)
    },
    doneSfAChecksCount() {
      return doneChecks(this.checks, this.filteredSfAssistantRemarks).length
    },
    pendingSfAChecksCount() {
      return pendingChecks(this.checks, this.filteredSfAssistantRemarks).length
    },
  },

  methods: {
    ...mapActions(['filterCards', 'unsetHighlightedCard']),

    filterCommsCards(type) {
      this.filterCards(type)
      this.unsetHighlightedCard()
      window.eventHub.$emit('clearAnchor')
    },

    handleItemClick(type) {
      this.filterCommsCards(type)
    },
  },
}
</script>
